/**
 * ACADEMY analytics dashboard (F4-C). Sections: enrollment/funnel,
 * revenue (dual USD/VES), debt (aging), attendance and academic + media
 * and engagement KPIs. Reuses the theme-aware mini-charts (`BarChart`) and the
 * `Money` component; no new chart libraries. Loading skeletons + i18n.
 *
 * `AcademyMetricsSections` is the reusable block: consumed by the
 * `academy.index` route (the academy's dashboard) and platform's `admin.metrics`
 * (active organization rollup).
 */
import { For, type JSX, Show } from "solid-js";
import { msg } from "@lingui/core/macro";
import type { MessageDescriptor } from "@lingui/core";
import {
  Award,
  BarChart3,
  CalendarClock,
  Contact,
  DollarSign,
  GraduationCap,
  TriangleAlert,
  Users,
  Video,
} from "@fleet/icons";
import { useI18n } from "@/providers/i18n-provider";
import { Card, CardContent, Skeleton } from "@fleet/ui";
import { BarChart, type ChartSegment } from "@/components/ui/mini-chart";
import { Money } from "@/features/finance/components/money";
import { STAGES as FUNNEL_ORDER } from "@/features/crm/api";
import { stageLabel } from "@/features/crm/labels";
import { useAcademyMetrics } from "./api";
import {
  type AgingBucketKey,
  certificatesTotal,
  formatBytes,
  funnelConversion,
  minorToUsd,
  orderedAgingBuckets,
  overdueTotalMinor,
  revenueByCurrency,
  revenueByMethod,
  stageCount,
} from "./selectors";

const pct = (n: number | null | undefined): string =>
  n === null || n === undefined ? "—" : `${Math.round(n * 100)}%`;

// The funnel order and the stage labels are the CRM's, not this dashboard's.
// Both were copied here verbatim; two copies of the same five labels is two
// places to forget when a stage is added.

const METHOD_LABEL: Record<string, MessageDescriptor> = {
  cash: msg`Cash`,
  pago_movil: msg`Mobile payment`,
  transfer_ves: msg`Transfer (VES)`,
  zelle: msg`Zelle`,
  stripe: msg`Card (Stripe)`,
};

const AGING_LABEL: Record<AgingBucketKey, MessageDescriptor> = {
  current: msg`Current`,
  "1_30": msg`1–30 days`,
  "31_60": msg`31–60 days`,
  "61_90": msg`61–90 days`,
  "90_plus": msg`90+ days`,
};

interface Props {
  orgId: () => string | undefined;
}

export function AcademyMetricsSections(props: Props) {
  const { t } = useI18n();
  const orgId = (): string | undefined => props.orgId();
  const query = useAcademyMetrics(orgId);

  return (
    <Show when={orgId()} fallback={<NoOrg />}>
      <Show
        when={!query.isLoading}
        fallback={
          <div class="space-y-4">
            <div class="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
              <For each={Array.from({ length: 8 }, () => 0)}>
                {() => <Skeleton class="h-24 w-full rounded-xl" />}
              </For>
            </div>
            <Skeleton class="h-56 w-full rounded-xl" />
            <Skeleton class="h-56 w-full rounded-xl" />
          </div>
        }
      >
        <Show
          when={query.data}
          fallback={
            <p class="text-sm text-muted-foreground">{t(msg`Metrics could not be loaded.`)}</p>
          }
        >
          {(m) => (
            <div class="space-y-8">
              {/* ── KPIs ─────────────────────────────────────────────────── */}
              <div class="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
                <Kpi label={t(msg`Verified revenue`)} icon={DollarSign}>
                  <Money usdMinor={m().revenue.totalUsdMinor} showRate={false} />
                </Kpi>
                <Kpi label={t(msg`Outstanding debt`)} icon={TriangleAlert} tone="warning">
                  <Money usdMinor={m().debt.totalUsdMinor} showRate={false} />
                </Kpi>
                <Kpi label={t(msg`Avg. attendance`)} icon={CalendarClock}>
                  <span class="text-2xl font-bold tabular-nums">{pct(m().attendance.avgRate)}</span>
                </Kpi>
                <Kpi label={t(msg`At-risk students`)} icon={TriangleAlert} tone="warning">
                  <span class="text-2xl font-bold tabular-nums">
                    {m().attendance.atRiskCount.toLocaleString()}
                  </span>
                </Kpi>
                <Kpi label={t(msg`Active (7d)`)} icon={Users}>
                  <span class="text-2xl font-bold tabular-nums">
                    {m().engagement.active7d.toLocaleString()}
                  </span>
                </Kpi>
                <Kpi label={t(msg`Active (30d)`)} icon={Users}>
                  <span class="text-2xl font-bold tabular-nums">
                    {m().engagement.active30d.toLocaleString()}
                  </span>
                </Kpi>
                <Kpi label={t(msg`Certificates`)} icon={Award}>
                  <span class="text-2xl font-bold tabular-nums">
                    {certificatesTotal(m()).toLocaleString()}
                  </span>
                </Kpi>
                <Kpi label={t(msg`Videos / storage`)} icon={Video}>
                  <span class="text-2xl font-bold tabular-nums">
                    {m().media.videoCount.toLocaleString()}
                  </span>
                  <span class="ml-1 text-sm text-muted-foreground">
                    · {formatBytes(m().media.storageBytes)}
                  </span>
                </Kpi>
              </div>

              {/* ── Enrollment / funnel ─────────────────────────────────────────────── */}
              <Section icon={Contact} title={t(msg`Enrollment funnel`)}>
                <Card>
                  <CardContent class="space-y-3 p-5">
                    <div class="flex items-center justify-between">
                      <span class="text-sm font-semibold">{t(msg`Leads by stage`)}</span>
                      <span class="text-sm text-muted-foreground">
                        {t(msg`Conversion`)}: {pct(funnelConversion(m().enrollment))}
                      </span>
                    </div>
                    <BarChart
                      emptyLabel={t(msg`No leads yet.`)}
                      segments={FUNNEL_ORDER.map<ChartSegment>((stage) => ({
                        label: t(stageLabel(stage)),
                        value: stageCount(m().enrollment, stage),
                        color: "var(--primary)",
                      }))}
                    />
                  </CardContent>
                </Card>
              </Section>

              {/* ── Ingresos ─────────────────────────────────────────────── */}
              <Section icon={DollarSign} title={t(msg`Revenue`)}>
                <div class="grid gap-4 lg:grid-cols-2">
                  <Card>
                    <CardContent class="space-y-3 p-5">
                      <span class="text-sm font-semibold">{t(msg`By payment method`)}</span>
                      <BarChart
                        emptyLabel={t(msg`No verified payments yet.`)}
                        segments={revenueByMethod(m().revenue).map<ChartSegment>((r) => ({
                          label: t(METHOD_LABEL[r.method] ?? msg`—`),
                          value: Math.round(minorToUsd(r.usdMinor)),
                          color: "var(--success)",
                        }))}
                      />
                    </CardContent>
                  </Card>
                  <Card>
                    <CardContent class="space-y-3 p-5">
                      <span class="text-sm font-semibold">{t(msg`By original currency`)}</span>
                      <div class="space-y-2">
                        <For
                          each={Object.entries(revenueByCurrency(m().revenue))}
                          fallback={
                            <p class="text-sm text-muted-foreground">
                              {t(msg`No verified payments yet.`)}
                            </p>
                          }
                        >
                          {([currency, usdMinor]) => (
                            <div class="flex items-center justify-between text-sm">
                              <span class="font-medium">{currency}</span>
                              <Money usdMinor={usdMinor} showRate={false} />
                            </div>
                          )}
                        </For>
                      </div>
                    </CardContent>
                  </Card>
                </div>
              </Section>

              {/* ── Deuda (aging) ────────────────────────────────────────── */}
              <Section icon={TriangleAlert} title={t(msg`Debt aging`)}>
                <Card>
                  <CardContent class="space-y-3 p-5">
                    <div class="flex items-center justify-between">
                      <span class="text-sm font-semibold">{t(msg`Outstanding by bucket`)}</span>
                      <span class="text-sm text-muted-foreground">
                        {t(msg`Overdue`)}:{" "}
                        <Money usdMinor={overdueTotalMinor(m().debt)} showRate={false} />
                      </span>
                    </div>
                    <div class="space-y-2">
                      <For each={orderedAgingBuckets(m().debt)}>
                        {(b) => (
                          <div class="flex items-center justify-between text-sm">
                            <span
                              class={
                                b.bucket === "current"
                                  ? "text-muted-foreground"
                                  : "font-medium text-warning"
                              }
                            >
                              {t(AGING_LABEL[b.bucket])}
                              <span class="ml-1 text-xs text-muted-foreground">({b.count})</span>
                            </span>
                            <Money usdMinor={b.usdMinor} showRate={false} />
                          </div>
                        )}
                      </For>
                    </div>
                  </CardContent>
                </Card>
              </Section>

              {/* ── Asistencia ───────────────────────────────────────────── */}
              <Section icon={CalendarClock} title={t(msg`Attendance`)}>
                <Card>
                  <CardContent class="space-y-3 p-5">
                    <span class="text-sm font-semibold">{t(msg`Lowest-attendance cohorts`)}</span>
                    <Show
                      when={m().attendance.worstCohorts.length > 0}
                      fallback={
                        <p class="text-sm text-muted-foreground">
                          {t(msg`No attendance recorded yet.`)}
                        </p>
                      }
                    >
                      <BarChart
                        segments={m().attendance.worstCohorts.map<ChartSegment>((c) => ({
                          label: c.name,
                          value: Math.round(c.rate * 100),
                          color: "var(--warning)",
                        }))}
                      />
                    </Show>
                  </CardContent>
                </Card>
              </Section>

              {/* ── Academic ─────────────────────────────────────────────────────────────── */}
              <Section icon={GraduationCap} title={t(msg`Academic`)}>
                <div class="grid gap-4 lg:grid-cols-2">
                  <Card>
                    <CardContent class="space-y-3 p-5">
                      <span class="text-sm font-semibold">{t(msg`Completion by cohort`)}</span>
                      <Show
                        when={m().academic.completionByCohort.length > 0}
                        fallback={
                          <p class="text-sm text-muted-foreground">{t(msg`No cohorts yet.`)}</p>
                        }
                      >
                        <BarChart
                          segments={m().academic.completionByCohort.map<ChartSegment>((c) => ({
                            label: c.name,
                            value: Math.round(c.completionRate * 100),
                            color: "var(--primary)",
                          }))}
                        />
                      </Show>
                    </CardContent>
                  </Card>
                  <Card>
                    <CardContent class="space-y-3 p-5">
                      <span class="text-sm font-semibold">
                        {t(msg`Avg. final grade by scheme`)}
                      </span>
                      <Show
                        when={m().academic.avgFinalByScheme.length > 0}
                        fallback={
                          <p class="text-sm text-muted-foreground">
                            {t(msg`No final grades yet.`)}
                          </p>
                        }
                      >
                        <div class="space-y-2">
                          <For each={m().academic.avgFinalByScheme}>
                            {(s) => (
                              <div class="flex items-center justify-between text-sm">
                                <span class="font-medium">{s.scheme}</span>
                                <span class="tabular-nums">
                                  {s.avg.toFixed(1)}
                                  <span class="ml-1 text-xs text-muted-foreground">
                                    ({s.count})
                                  </span>
                                </span>
                              </div>
                            )}
                          </For>
                        </div>
                      </Show>
                    </CardContent>
                  </Card>
                </div>
              </Section>
            </div>
          )}
        </Show>
      </Show>
    </Show>
  );
}

function NoOrg() {
  const { t } = useI18n();
  return (
    <div class="flex flex-col items-center gap-2 rounded-xl border border-dashed p-10 text-center">
      <BarChart3 class="h-8 w-8 text-muted-foreground" />
      <p class="text-sm text-muted-foreground">{t(msg`Select an academy to see its metrics.`)}</p>
    </div>
  );
}

interface KpiProps {
  label: string;
  icon: typeof DollarSign;
  tone?: "warning";
  children: JSX.Element;
}

function Kpi(props: KpiProps) {
  return (
    <Card>
      <CardContent class="space-y-2 p-5">
        <div class="flex items-center justify-between">
          <span class="text-sm text-muted-foreground">{props.label}</span>
          <props.icon
            class={props.tone === "warning" ? "h-4 w-4 text-warning" : "h-4 w-4 text-primary"}
          />
        </div>
        <div class="text-lg font-bold tracking-tight">{props.children}</div>
      </CardContent>
    </Card>
  );
}

interface SectionProps {
  icon: typeof DollarSign;
  title: string;
  children: JSX.Element;
}

function Section(props: SectionProps) {
  return (
    <section class="space-y-3">
      <div class="flex items-center gap-2">
        <props.icon class="h-5 w-5 text-primary" />
        <h3 class="text-lg font-bold tracking-tight">{props.title}</h3>
      </div>
      {props.children}
    </section>
  );
}
